import { ClinicEventType } from '../enums/ClinicEventType.enum';

/**
 * Aggregation methods for metric computation.
 *
 * - count: COUNT(*) of matching events
 * - distinct_users: COUNT(DISTINCT userId) — unique users
 * - first_occurrence: MIN(occurredAt) — stored as epoch ms in value
 * - last_occurrence: MAX(occurredAt) — stored as epoch ms in value
 */
export type AggregationMethod = 'count' | 'distinct_users' | 'first_occurrence' | 'last_occurrence';

/**
 * Scope determines how events are grouped:
 *
 * - clinic: one summary row per clinic per day (userId = '__clinic__')
 *           Used for system/patient events with no provider userId,
 *           or for distinct-count aggregations.
 * - user: one summary row per userId per clinic per day
 *         Used for provider actions — can SUM across users to get clinic totals.
 */
export type MetricScope = 'clinic' | 'user';

/**
 * A single stat definition. Adding a new stat = adding one object to the array below.
 *
 * metricKey: the key stored in ClinicMetricSummary.metricKey
 * eventTypes: which ClinicEvent.eventType values to match (OR)
 * aggregation: how to aggregate matching events
 * scope: aggregate per clinic or per user
 */
export type MetricDefinition = {
  metricKey: string;
  eventTypes: ClinicEventType[];
  aggregation: AggregationMethod;
  scope: MetricScope;
};

/**
 * Sentinel value for clinic-level metrics where userId is not applicable.
 * Used instead of NULL to ensure the UNIQUE index works correctly for upserts.
 */
export const CLINIC_SCOPE_USER_ID = '__clinic__';

/**
 * All metric definitions. Single source of truth for metrics across backend and frontend.
 *
 * Used by:
 * - chiroup-backend-watchers: aggregates metrics into ClinicMetricSummary
 * - chiroup-admin-backend: tracks aggregation rules
 * - chiroup-admin frontend: maps metrics to event types for the drawer
 *
 * To add a new metric: add one entry here. The mapping is automatically derived.
 */
export const metricDefinitions: MetricDefinition[] = [
  // ── Schedule ──
  {
    metricKey: 'appointments_created',
    eventTypes: [ClinicEventType.AppointmentCreated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'appointments_checked_in',
    eventTypes: [ClinicEventType.AppointmentCheckedIn],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'appointments_checked_out',
    eventTypes: [ClinicEventType.AppointmentCheckedOut],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Encounters ──
  {
    metricKey: 'encounters_created',
    eventTypes: [ClinicEventType.EncounterCreated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'encounters_signed',
    eventTypes: [ClinicEventType.EncounterSigned],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'voice_to_chart_uses',
    eventTypes: [ClinicEventType.VoiceToChartUsed],
    aggregation: 'count',
    scope: 'user',
  },

  // ── PCP Reports ──
  {
    metricKey: 'pcp_initial_reports',
    eventTypes: [ClinicEventType.PcpInitialReportCreated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'pcp_interim_reports',
    eventTypes: [ClinicEventType.PcpInterimReportCreated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'pcp_release_reports',
    eventTypes: [ClinicEventType.PcpReleaseReportCreated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Surveys (user-initiated) ──
  {
    metricKey: 'ccs_sent',
    eventTypes: [ClinicEventType.CcsSent],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Surveys (system/patient — no provider userId) ──
  {
    metricKey: 'ccs_completed',
    eventTypes: [ClinicEventType.CcsCompleted],
    aggregation: 'count',
    scope: 'clinic',
  },
  {
    metricKey: 'post_care_surveys_sent',
    eventTypes: [ClinicEventType.PostCareSurveySent],
    aggregation: 'count',
    scope: 'clinic',
  },
  {
    metricKey: 'post_care_surveys_completed',
    eventTypes: [ClinicEventType.PostCareSurveyCompleted],
    aggregation: 'count',
    scope: 'clinic',
  },

  // ── Billing & Claims ──
  {
    metricKey: 'purchases_created',
    eventTypes: [ClinicEventType.PurchaseCreated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'claims_sent',
    eventTypes: [ClinicEventType.ClaimSent],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'claims_accepted',
    eventTypes: [ClinicEventType.ClaimAccepted],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'payments_collected',
    eventTypes: [ClinicEventType.PaymentCollected],
    aggregation: 'count',
    scope: 'user',
  },

  // ── EDI (system — no userId) ──
  {
    metricKey: 'eras_uploaded',
    eventTypes: [ClinicEventType.EraUploaded],
    aggregation: 'count',
    scope: 'clinic',
  },
  {
    metricKey: 'npi_rejections',
    eventTypes: [ClinicEventType.NpiRejection],
    aggregation: 'count',
    scope: 'clinic',
  },
  {
    metricKey: 'enrollment_rejections',
    eventTypes: [ClinicEventType.EnrollmentRejection],
    aggregation: 'count',
    scope: 'clinic',
  },
  {
    metricKey: 'format_837_rejections',
    eventTypes: [ClinicEventType.Format837Rejection],
    aggregation: 'count',
    scope: 'clinic',
  },
  {
    metricKey: 'provider_loop_errors',
    eventTypes: [ClinicEventType.ProviderLoopError],
    aggregation: 'count',
    scope: 'clinic',
  },
  {
    metricKey: 'referring_provider_errors',
    eventTypes: [ClinicEventType.ReferringProviderError],
    aggregation: 'count',
    scope: 'clinic',
  },

  // ── Clinical Skills ──
  {
    metricKey: 'condition_references_accessed',
    eventTypes: [ClinicEventType.ConditionReferenceAccessed],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'condition_references_edited',
    eventTypes: [ClinicEventType.ConditionReferenceEdited],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'clinical_evaluations_accessed',
    eventTypes: [ClinicEventType.ClinicalEvaluationAccessed],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'treatment_techniques_accessed',
    eventTypes: [ClinicEventType.TreatmentTechniqueAccessed],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Practice Resources ──
  {
    metricKey: 'marketing_campaigns_accessed',
    eventTypes: [ClinicEventType.MarketingCampaignAccessed],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'forms_library_downloads',
    eventTypes: [ClinicEventType.FormsLibraryDownloaded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'research_reviews_accessed',
    eventTypes: [ClinicEventType.ResearchReviewAccessed],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Patient ──
  {
    metricKey: 'patients_created',
    eventTypes: [ClinicEventType.PatientCreated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Communications ──
  {
    metricKey: 'condition_reports_sent',
    eventTypes: [ClinicEventType.ConditionReportSent],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'google_review_links_sent',
    eventTypes: [ClinicEventType.GoogleReviewLinkSent],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Communications (system/patient — no provider userId) ──
  {
    metricKey: 'condition_reports_opened',
    eventTypes: [ClinicEventType.ConditionReportOpened],
    aggregation: 'count',
    scope: 'clinic',
  },

  // ── Clinic Settings ──
  {
    metricKey: 'clinic_logo_added',
    eventTypes: [ClinicEventType.ClinicLogoAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'facebook_linked',
    eventTypes: [ClinicEventType.FacebookLinked],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'google_review_url_added',
    eventTypes: [ClinicEventType.GoogleReviewUrlAdded],
    aggregation: 'count',
    scope: 'user',
  },

  // ── User Settings ──
  {
    metricKey: 'billing_info_added',
    eventTypes: [ClinicEventType.BillingInfoAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'commercial_numbers_added',
    eventTypes: [ClinicEventType.CommercialNumbersAdded],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Support ──
  {
    metricKey: 'features_suggested',
    eventTypes: [ClinicEventType.FeatureSuggested],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'features_voted',
    eventTypes: [ClinicEventType.FeatureVoted],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Knowledge Base ──
  {
    metricKey: 'knowledge_base_accessed',
    eventTypes: [ClinicEventType.KnowledgeBaseAccessed],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Healthcom (patient — distinct count) ──
  {
    metricKey: 'healthcom_logins',
    eventTypes: [ClinicEventType.HealthcomLogin],
    aggregation: 'distinct_users',
    scope: 'clinic',
  },

  // ── Activity (distinct count) ──
  {
    metricKey: 'active_users',
    eventTypes: [ClinicEventType.Heartbeat],
    aggregation: 'distinct_users',
    scope: 'clinic',
  },

  // Note: Login events have clinicId=0 (user-level, no clinic context at auth time).
  // Login counts are queried directly from ClinicEvent, not aggregated here.
  // Clinic-level activity is covered by the 'active_users' heartbeat metric above.

  // ── Subscription & Users ──
  {
    metricKey: 'plan_changes',
    eventTypes: [ClinicEventType.PlanChanged],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'user_role_changes',
    eventTypes: [ClinicEventType.UserRoleChanged],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'users_removed',
    eventTypes: [ClinicEventType.UserRemovedFromClinic],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'users_added',
    eventTypes: [ClinicEventType.UserAddedToClinic],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Clinic Settings (expanded) ──
  {
    metricKey: 'clinic_locations_added',
    eventTypes: [ClinicEventType.ClinicLocationAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'clinic_schedule_updates',
    eventTypes: [ClinicEventType.ClinicScheduleUpdated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'survey_preferences_updates',
    eventTypes: [ClinicEventType.SurveyPreferencesUpdated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'scheduling_preferences_updates',
    eventTypes: [ClinicEventType.SchedulingPreferencesUpdated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'billing_global_settings_updates',
    eventTypes: [ClinicEventType.BillingGlobalSettingsUpdated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Provider Settings ──
  {
    metricKey: 'provider_billing_updates',
    eventTypes: [ClinicEventType.ProviderBillingUpdated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'provider_schedule_updates',
    eventTypes: [ClinicEventType.ProviderScheduleUpdated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'provider_signatures_added',
    eventTypes: [ClinicEventType.ProviderSignatureAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'provider_signature_deletes',
    eventTypes: [ClinicEventType.ProviderSignatureDeleted],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'provider_profile_picture_updates',
    eventTypes: [ClinicEventType.ProviderProfilePictureUpdated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Online Booking ──
  {
    metricKey: 'online_scheduling_url_updates',
    eventTypes: [ClinicEventType.OnlineSchedulingUrlUpdated],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'online_bookings_user_toggles',
    eventTypes: [ClinicEventType.OnlineBookingsUserToggled],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'online_booking_treatment_updates',
    eventTypes: [ClinicEventType.OnlineBookingTreatmentUpdated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Database / Library Adds ──
  {
    metricKey: 'payors_added',
    eventTypes: [ClinicEventType.PayorAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'billing_codes_added',
    eventTypes: [ClinicEventType.BillingCodeAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'modalities_added',
    eventTypes: [ClinicEventType.ModalityAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'supplements_added',
    eventTypes: [ClinicEventType.SupplementAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'healthcare_providers_added',
    eventTypes: [ClinicEventType.HealthcareProviderAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'tests_added',
    eventTypes: [ClinicEventType.TestAdded],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Fees ──
  {
    metricKey: 'no_show_cancel_fees_added',
    eventTypes: [ClinicEventType.NoShowCancelFeeAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'no_show_cancel_fees_updated',
    eventTypes: [ClinicEventType.NoShowCancelFeeUpdated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Packages ──
  {
    metricKey: 'packages_added',
    eventTypes: [ClinicEventType.PackageAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'packages_updated',
    eventTypes: [ClinicEventType.PackageUpdated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Disciplines ──
  {
    metricKey: 'disciplines_added',
    eventTypes: [ClinicEventType.DisciplineAdded],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'disciplines_updated',
    eventTypes: [ClinicEventType.DisciplineUpdated],
    aggregation: 'count',
    scope: 'user',
  },

  // ── Kiosk ──
  {
    metricKey: 'kiosk_connections',
    eventTypes: [ClinicEventType.KioskConnected],
    aggregation: 'count',
    scope: 'user',
  },
  {
    metricKey: 'kiosk_disconnections',
    eventTypes: [ClinicEventType.KioskDisconnected],
    aggregation: 'count',
    scope: 'user',
  },

  // ── User-Level Events (clinicId=0, no clinic context) ──
  {
    metricKey: 'logins',
    eventTypes: [ClinicEventType.Login],
    aggregation: 'count',
    scope: 'user',
  },
];

/**
 * Derives metricKey → eventType map from metricDefinitions.
 * Each metric maps to its primary eventType (eventTypes[0]).
 *
 * Single source of truth: when metricDefinitions is updated,
 * this map is automatically generated. No manual sync needed.
 */
export const buildMetricToEventTypeMap = (): Record<string, string> => {
  return metricDefinitions.reduce(
    (acc, def) => ({
      ...acc,
      [def.metricKey]: def.eventTypes[0],
    }),
    {}
  );
};
